import { ErrorState, LoadingState } from '@playforge/ui';
import { Badge, Banner, BlockStack, Button, Card, List, Page, Text } from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../lib/api-client';
import { useApi } from '../lib/use-api';
import type { FeaturesView } from '../lib/types';

/**
 * Developer settings surface. The Developer backend module (API keys, webhooks,
 * SDKs) is Phase 2, so this page communicates availability and gates on the
 * plan's `api_access` feature rather than issuing keys.
 */
export function DeveloperSettingsPage(): JSX.Element {
  const navigate = useNavigate();
  const { data, loading, error, refetch } = useApi(
    () => apiClient.get<FeaturesView>('/billing/features'),
    [],
  );

  const hasApiAccess = data?.features.includes('api_access') ?? false;

  return (
    <Page title="Developers" subtitle="APIs, webhooks and SDKs">
      {loading ? <LoadingState label="Loading developer settings" /> : null}
      {error ? <ErrorState message={error.message} onRetry={refetch} /> : null}
      {data ? (
        <BlockStack gap="500">
          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">
                API access
              </Text>
              {hasApiAccess ? (
                <Badge tone="success">Included in your plan</Badge>
              ) : (
                <Badge tone="attention">Requires an upgrade</Badge>
              )}
              {!hasApiAccess ? (
                <Button onClick={() => navigate('/billing')}>View plans</Button>
              ) : null}
            </BlockStack>
          </Card>

          <Card>
            <BlockStack gap="300">
              <Text as="h2" variant="headingMd">
                Coming in Phase 2
              </Text>
              <List>
                <List.Item>REST API keys</List.Item>
                <List.Item>Outgoing webhooks</List.Item>
                <List.Item>Request logs</List.Item>
                <List.Item>JavaScript, React, Flutter & React Native SDKs</List.Item>
              </List>
              <Banner tone="info" title="Developer platform is on the roadmap">
                <p>
                  These tools arrive in Phase 2. The Core Platform already exposes versioned REST
                  APIs that the SDKs will consume.
                </p>
              </Banner>
            </BlockStack>
          </Card>
        </BlockStack>
      ) : null}
    </Page>
  );
}
